import React, { useEffect, useState } from "react";
import { Drawer, Transfer, Input, Button, Typography, Empty } from "antd";
import { CloseOutlined, ReloadOutlined } from "@ant-design/icons";
import styles from "./FunctionManagement.css";
import DynamicFormFields from "./DynamicFormFields";
import { Api } from "@/api/api";
import { FormattedMessage, injectIntl } from "react-intl";

const { TextArea } = Input;
const { Text } = Typography;

const FunctionManagementDrawer = ({
    functions,
    allFunctions,
    agentId,
    open,
    onClose,
    onSave,
    intl,
}) => {
    // 穿梭框数据
    const [transferData, setTransferData] = useState(
        allFunctions.map((item) => {
            return {
                key: item.id,
                title: item.name,
                description: item.description,
                ...item,
            };
        })
    );

    const [mcpUrl, setMcpUrl] = useState("");

    // 已选择的功能
    const [selectedKeys, setSelectedKeys] = useState([]);

    useEffect(() => {
        if (functions?.length) {
            setSelectedKeys(functions.map((item) => item.id));
            setActivedKey(functions[0].id);
        }
    }, [functions]);

    useEffect(() => {
        if (agentId) {
            loadMcpAddress(agentId);
            setMcpStatus("loading");
            loadMcpTools(agentId);
        }
    }, [agentId]);

    // MCP 配置
    const [mcpConfig, setMcpConfig] = useState("");
    const [activedKey, setActivedKey] = useState("");
    const [mcpTools, setMcpTools] = useState([]);
    const [mcpStatus, setMcpStatus] = useState("disconnected");

    const activedFunctions =
        transferData.find((item) => item.id === activedKey) || {};

    // 连接状态
    const [connectionStatus] = useState("disconnected"); // 'disconnected' | 'connected'

    // 处理穿梭框变化
    const handleTransferChange = (targetKeys) => {
        setSelectedKeys(targetKeys);
    };

    // 处理保存
    const handleSave = () => {
        const updatedFunction = transferData.filter((item) =>
            selectedKeys.includes(item.key)
        );
        const saveData = {
            selectedFunctions: selectedKeys,
            updatedFunction: updatedFunction,
            mcpConfig,
        };
        onSave?.(saveData);
    };

    // 复制MCP配置
    const handleCopyMcp = () => {
        navigator.clipboard.writeText(mcpUrl || "");
    };

    const handleClickKey = (key) => {
        if (selectedKeys.includes(key)) {
            setActivedKey(key);
        }
    };

    const handleParamChange = (func, key, value) => {
        setTransferData((prevData) => {
            return prevData.map((item) => {
                if (item.name === func.name) {
                    const newItem = JSON.parse(JSON.stringify(func));
                    newItem.params[key] = value;
                    return newItem;
                }

                return item;
            });
        });
    };

    const loadMcpAddress = (id) => {
        Api.agent.getAgentMcpAccessAddress(id, (res) => {
            if (res.data.code === 0) {
                setMcpUrl(res.data.data || "");
            } else {
                setMcpUrl("");
                console.error("获取MCP地址失败:", res.data.msg);
            }
        });
    };

    // 加载MCP工具列表
    const loadMcpTools = (id) => {
        Api.agent.getAgentMcpToolsList(id, (res) => {
            if (res.data.code === 0) {
                const tempTools = res.data.data || [];
                setMcpTools(tempTools);
                // 根据工具列表更新状态
                setMcpStatus(
                    tempTools.length > 0 ? "connected" : "disconnected"
                );
            } else {
                setMcpTools([]);
                setMcpStatus("disconnected");
                console.error("获取MCP工具列表失败:", res.data.msg);
            }
        });
    };

    const refreshStatusFn = () => {
        setMcpStatus("loading");
        loadMcpTools(agentId);
    };

    return (
        <Drawer
            title={intl.formatMessage({ id: "gui.agentManage.functionManagement", defaultMessage: "Function Management" })}
            width={"86%"}
            open={open}
            onClose={onClose}
            closeIcon={<CloseOutlined />}
            className={styles.drawer}
            footer={
                <div className={styles.drawerFooter}>
                    <Button size={"large"} onClick={onClose}>
                        <FormattedMessage id="gui.agentManage.cancel" defaultMessage="Cancel" />
                    </Button>
                    <Button size={"large"} type="primary" onClick={handleSave}>
                        <FormattedMessage id="gui.agentManage.saveConfig" defaultMessage="Save Configuration" />
                    </Button>
                </div>
            }
        >
            <div className={styles.drawerBody}>
                <div className={styles.mainContent}>
                    {/* 左侧穿梭框区域 */}
                    <div className={styles.transferSection}>
                        <div className={styles.transfer}>
                            <Transfer
                                dataSource={transferData}
                                targetKeys={selectedKeys}
                                onChange={handleTransferChange}
                                render={(item) => (
                                    <div
                                        onClick={() => handleClickKey(item.key)}
                                    >
                                        {item.title}
                                    </div>
                                )}
                                titles={[intl.formatMessage({ id: "gui.agentManage.unselectedFunctions", defaultMessage: "Unselected Functions" }), intl.formatMessage({ id: "gui.agentManage.selectedFunctions", defaultMessage: "Selected Functions" })]}
                                showSelectAll={true}
                                selectAllLabels={[intl.formatMessage({ id: "gui.agentManage.selectAll", defaultMessage: "Select All" }), intl.formatMessage({ id: "gui.agentManage.selectAll", defaultMessage: "Select All" })]}
                            />
                        </div>
                    </div>

                    {/* 右侧配置区域 */}
                    <div className={styles.configSection}>
                        <div className={styles.sectionTitle}>
                            <FormattedMessage id="gui.agentManage.paramConfig" defaultMessage="Parameter Configuration" /> - HomeAssistant<FormattedMessage id="gui.agentManage.deviceStatusQuery" defaultMessage="Device Status Query" />
                        </div>

                        <div
                            style={{
                                overflowY: "auto",
                                height: "calc(100% - 40px)",
                                paddingRight: "20px",
                            }}
                        >
                            {!activedFunctions?.fieldsMeta?.length ? (
                                <Empty
                                    style={{ paddingTop: 60 }}
                                    description={intl.formatMessage({ id: "gui.agentManage.noConfigNeeded", defaultMessage: "No configuration needed" })}
                                />
                            ) : (
                                <>
                                    <DynamicFormFields
                                        currentFunction={activedFunctions}
                                        handleParamChange={handleParamChange}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* 底部区域 */}
                <div className={styles.bottomSection}>
                    <div className={styles.mcpSection}>
                        <div className={styles.mcpLeft}>
                            <div className={styles.mcpTitle}><FormattedMessage id="gui.agentManage.mcpEndpoint" defaultMessage="MCP Endpoint" /></div>
                            <div className={styles.mcpDescription}>
                                <FormattedMessage id="gui.agentManage.mcpDescription" defaultMessage="Below is the MCP endpoint address of the agent." />
                                <div className={styles.mcpLinks}>
                                    <a
                                        href="https://github.com/xinnan-tech/xiaozhi-esp32-server/blob/main/docs/mcp-endpoint-enable.md"
                                        className={styles.mcpLink}
                                    >
                                        <FormattedMessage id="gui.agentManage.howToDeployMcp" defaultMessage="How to deploy MCP endpoint" />
                                    </a>
                                    <span style={{ padding: "0 8px" }}>|</span>
                                    <a
                                        href="https://github.com/xinnan-tech/xiaozhi-esp32-server/blob/main/docs/mcp-endpoint-integration.md"
                                        className={styles.mcpLink}
                                    >
                                        <FormattedMessage id="gui.agentManage.howToIntegrateMcp" defaultMessage="How to integrate MCP functions" />
                                    </a>
                                </div>
                            </div>
                            <div className={styles.mcpInput}>
                                <Input
                                    value={mcpUrl}
                                    placeholder={intl.formatMessage({ id: "gui.agentManage.mcpPlaceholder", defaultMessage: "Please contact administrator to configure MCP endpoint address in parameter management" })}
                                    readOnly
                                    style={{ flex: 1 }}
                                />
                                <Button type="primary" onClick={handleCopyMcp}>
                                    <FormattedMessage id="gui.agentManage.copy" defaultMessage="Copy" />
                                </Button>
                            </div>
                        </div>

                        <div className={styles.mcpRight}>
                            <div className={styles.mcpTitle}><FormattedMessage id="gui.agentManage.endpointStatus" defaultMessage="Endpoint Status" /></div>
                            <div className={styles.statusSection}>
                                <div className={styles.statusItem}>
                                    <div
                                        className={`${styles.statusDot} ${
                                            connectionStatus === "connected"
                                                ? styles.statusDotGreen
                                                : styles.statusDotGray
                                        }`}
                                    />
                                    <span>
                                        {mcpStatus === "connected"
                                            ? intl.formatMessage({ id: "gui.agentManage.connected", defaultMessage: "Connected" })
                                            : mcpStatus === "loading"
                                            ? intl.formatMessage({ id: "gui.agentManage.loading", defaultMessage: "Loading..." })
                                            : intl.formatMessage({ id: "gui.agentManage.disconnected", defaultMessage: "Disconnected" })}
                                    </span>
                                </div>
                                <div className={styles.statusItem}>
                                    <Button
                                        icon={<ReloadOutlined />}
                                        size={"small"}
                                        onClick={refreshStatusFn}
                                    >
                                        <FormattedMessage id="gui.agentManage.refresh" defaultMessage="Refresh" />
                                    </Button>
                                </div>
                            </div>
                            <div>
                                <div style={{ padding: "12px 0" }}>
                                    {mcpTools.map((item, index) => (
                                        <Button
                                            style={{
                                                marginRight: "8px",
                                                marginBottom: "12px",
                                            }}
                                            type={"primary"}
                                            key={index}
                                        >
                                            {item}
                                        </Button>
                                    ))}
                                </div>
                                {mcpTools.length === 0 && (
                                    <div className={styles.toolText}>
                                        <FormattedMessage id="gui.agentManage.noToolsAvailable" defaultMessage="No tools available" />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Drawer>
    );
};

export default injectIntl(FunctionManagementDrawer);